import { useCallback, useRef } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';

const LIBRARIES = ['places'];

// RAT-1931: which Google administrative level our "Province" maps to differs
// per country, so it is decided here rather than guessed at the call site.
//  - ES: level_2 is the province (Málaga); level_1 (the autonomous community,
//    e.g. Andalucía) is accepted as a fallback for postcodes where Google
//    returns no level_2, so a lookup still yields a Province instead of none
//  - NL: level_1 is the provincie (Noord-Holland)
//  - LU: level_1 is the canton
//  - GB: level_2 is the county (Greater London), level_1 only "England"
// Countries without an entry fall back to the generic chain below.
const PROVINCE_LEVELS_BY_COUNTRY = {
  ES: ['administrative_area_level_1'],
  NL: ['administrative_area_level_1'],
  LU: ['administrative_area_level_1'],
  GB: ['administrative_area_level_1'],
};
const DEFAULT_PROVINCE_LEVELS = [
  'administrative_area_level_2',
  'administrative_area_level_1',
];

// postal_town is what the UK returns instead of locality; level_3 is the last
// resort for countries that only expose the municipality at that level.
const CITY_LEVELS = ['locality', 'postal_town', 'administrative_area_level_3'];

// First component matching any of `types`, in the order the types are listed.
const pickComponent = (components, types) => {
  const match = types
    .map((type) => components.find((comp) => comp.types?.includes(type)))
    .find(Boolean);
  return match?.long_name || '';
};

// RAT-1931: Country + Postcode -> Province + City. Deliberately a separate hook
// from useAddressGeocodeCheck (address *validation*) and
// useGooglePlacesAutocomplete (address *search*) - this one resolves a postcode
// to its structured administrative components. Shares the same loader `id` so
// the Maps script is still only ever loaded once, whichever hook mounts first.
export const usePostcodeLocationLookup = () => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey:
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY_HERE',
    libraries: LIBRARIES,
  });

  const geocoderRef = useRef(null);

  // Resolves to { provinceName, cityName } on a hit, or null on anything else -
  // no match, no country/postcode, script not loaded, API error or timeout. The
  // caller degrades to plain manual entry on null (AC6/AC7), so failures never
  // surface an error or block the form.
  const lookupPostcodeLocation = useCallback(
    (countryCode, postalCode) =>
      new Promise((resolve) => {
        if (!isLoaded || !window.google || !countryCode || !postalCode) {
          resolve(null);
          return;
        }

        if (!geocoderRef.current) {
          geocoderRef.current = new window.google.maps.Geocoder();
        }

        geocoderRef.current.geocode(
          { componentRestrictions: { country: countryCode, postalCode } },
          (results, status) => {
            if (status !== 'OK' || !results?.length) {
              resolve(null);
              return;
            }

            const components = results[0].address_components || [];
            const provinceName = pickComponent(
              components,
              PROVINCE_LEVELS_BY_COUNTRY[countryCode] ||
                DEFAULT_PROVINCE_LEVELS,
            );
            const cityName = pickComponent(components, CITY_LEVELS);

            resolve(
              provinceName || cityName ? { provinceName, cityName } : null,
            );
          },
        );
      }),
    [isLoaded],
  );

  return { isLoaded, lookupPostcodeLocation };
};
